import { useLayers } from '@ais-ui/react-openlayers';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useState } from 'react';
import { GridJobInstance, useGPSLost } from './GPSLostContext';

interface UseGPSLostAddGridToMapResult {
  addGridToMap: (
    configuration: any,
    instance: GridJobInstance,
    zoom?: boolean,
  ) => Promise<void>;
  isAdding: boolean;
}

export const useGPSLostAddGridToMap = (): UseGPSLostAddGridToMapResult => {
  const {
    addLayer,
    addLayerAtIndex,
    getLayersByIdContaining,
    zoomToLayer,
    removeLayer,
    layers,
  } = useLayers();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const [isAdding, setIsAdding] = useState(false);
  const { createGridLayerAndSetSelectedInstance } = useGPSLost();

  // Function to check if an instance can be added to the map
  const canAddInstance = (instance: GridJobInstance | undefined): boolean => {
    if (!instance) return false;
    return instance.status === 'success' || instance.status === 'no_results';
  };

  const addGridToMap = async (
    configuration: any,
    instance: GridJobInstance,
    zoom = true,
  ) => {
    if (!instance || !canAddInstance(instance)) {
      console.warn(
        'Cannot add instance to map - invalid or unsuccessful instance',
      );
      toast({
        className: 'gps-lost-grid-invalid-grid-instance',
        title: messages.genericToastTitleWarning,
        description: `Invalid or unsuccessful instance: ${
          instance?.status || 'unknown'
        }`,
        variant: 'warning',
      });
      return;
    }

    const gpsLostGridConfigurationIdPart = `gps_lost_grid_${configuration.id}`;
    const gpsLostGridId = `${gpsLostGridConfigurationIdPart}_${instance.id}`;
    setIsAdding(true);

    try {
      // Capture the original GPS Lost layer position before doing anything
      const oldGPSLostGridLayers = getLayersByIdContaining(
        gpsLostGridConfigurationIdPart,
      );

      let originalIndex = -1;
      if (oldGPSLostGridLayers.length > 0) {
        const oldLayer = oldGPSLostGridLayers[0];
        originalIndex = layers.findIndex((layer) => layer.id === oldLayer.id);
      }

      const { layer, maxGpsLostValue } =
        await createGridLayerAndSetSelectedInstance(
          configuration,
          instance,
          gpsLostGridId,
        );

      // Remove existing layers from this configuration
      oldGPSLostGridLayers.forEach((layer) => {
        removeLayer(layer.layer);
      });

      const oldGPSLostTrackLayers = getLayersByIdContaining('gps-lost-track');
      oldGPSLostTrackLayers.forEach((layer) => {
        removeLayer(layer.layer);
      });

      // Add the new layer directly at the original position if we had one
      let newLayerItem;
      if (originalIndex !== -1) {
        newLayerItem = addLayerAtIndex(
          layer,
          `GPS Lost - ${configuration.job_name}`,
          originalIndex,
          true,
          true,
          false,
          {
            type: 'gps-lost',
            maxGpsLostValue,
          },
          gpsLostGridId,
          false,
          true,
          false,
        );
      } else {
        // Fallback to regular addLayer if no original position
        newLayerItem = addLayer(
          layer,
          `GPS Lost - ${configuration.job_name}`,
          true,
          true,
          false,
          {
            type: 'gps-lost',
            maxGpsLostValue,
          },
          gpsLostGridId,
          false,
          true,
          false,
        );
      }

      if (zoom) {
        zoomToLayer(layer);
      }

      toast({
        title: messages.genericToastTitleSuccess,
        description: 'GPS Lost Grid has been added to the map',
      });
    } catch (error) {
      console.error('Error fetching instance data:', error);
      toast({
        title: messages.genericToastTitleError,
        description: 'Error adding GPS Lost Grid to the map',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  return {
    addGridToMap,
    isAdding,
  };
};
